import { clearKeyValuePair, setKeyValuePair } from '@gik/ads/components/Ad';
import { useContentTags } from '@gik/api/blog/contentTags';
import { useTaxonomies } from '@gik/api/give/taxonomies';
import { useArticleType } from '@gik/api/posts/articleType';
import { useGeneralResults } from '@gik/api/posts/generalResults';
import { ArticleDynamicLoader } from '@gik/blog/components/ArticleDynamicLoader/ArticleDynamicLoader';
import { ArticlesGrid } from '@gik/blog/components/ArticlesGrid/ArticlesGrid';
import { BlogNav } from '@gik/blog/components/BlogNav/BlogNav';
import type Article from '@gik/blog/models/Article';
import type ArticleType from '@gik/blog/models/ArticleType';
import articleTypes from '@gik/blog/models/ArticleTypes';
import type Category from '@gik/blog/models/Category';
import Taxonomy from '@gik/blog/Taxonomy';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import { withTitle } from '@gik/core/hocs/withTitle';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import type WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import type WordpressTag from '@gik/core/models/wordpress/WordpressTag';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { useRandomInt } from '@gik/core/utils/getRandomInt';
import { ArticleHeader, ArticleTile } from '@gik/ui/gik/ArticleTile';
import { PageHeader } from '@gik/ui/gik/PageHeader';
import { PageSection } from '@gik/ui/gik/PageSection';
import { ParagraphBone } from '@gik/ui/SkeletonLoader';
import React from 'react';

export type IGeneralResultsProps = {
  generalResults: Article[];
  articleType: ArticleType;
};

const blockName = 'general-results';
const minCardsForFeatured = 5;
const smallCardsOffset = 3;

function GeneralResultsSkeleton() {
  const bem = useBemCN(blockName);
  const words = useRandomInt(3, 7);

  return (
    <article {...bem()}>
      <PageHeader variant={'aqua'} contained={false}>
        <h1 {...bem('page-name')}>
          <ParagraphBone words={words} />
        </h1>
      </PageHeader>

      <PageSection>
        <ArticlesGrid {...bem()} articles={Array.from(new Array(4))} skeleton />
      </PageSection>
    </article>
  );
}

function GeneralResultsComp({ articleType, generalResults }: IGeneralResultsProps) {
  const bem = useBemCN(blockName);

  React.useEffect(() => {
    setKeyValuePair(articleType.name);

    return () => {
      clearKeyValuePair();
    };
  }, [articleType]);

  return (
    <article {...bem()}>
      <PageHeader variant={'aqua'}>
        <h1 {...bem('page-name')}>{articleType.name}</h1>
      </PageHeader>

      {generalResults.length < minCardsForFeatured ? (
        <PageSection>
          <ArticleDynamicLoader articles={generalResults} />
        </PageSection>
      ) : (
        <>
          <PageSection {...bem('featured-wrapper')}>
            <ArticleHeader {...bem('featured-header')} article={generalResults?.[0]} noLink={false} />
            <ArticleTile {...bem('featured-tile')} article={generalResults?.[0]} layout={'column'} />
            <ArticlesGrid
              {...bem('section-2')}
              articles={generalResults.slice(1, smallCardsOffset)}
              tileLayout="column"
            />
          </PageSection>

          <PageSection {...bem('article-dynamic-loader')}>
            <ArticleDynamicLoader articles={generalResults.slice(smallCardsOffset)} />
          </PageSection>
        </>
      )}
    </article>
  );
}

export function getGeneralResultsPageName(
  taxonomySlug: string,
  pageSlug: string,
  tags?: Category[],
  situations?: WordpressSituation[]
): string {
  let pageName = null;
  if (taxonomySlug == Taxonomy.ContentTag) {
    const tag = tags?.find(t => t.slug === pageSlug);
    if (tag) {
      pageName = tag.name;
    }
  } else if (taxonomySlug == Taxonomy.Situation) {
    const situation = situations?.find(s => s.slug === pageSlug);
    if (situation) {
      pageName = situation.name;
    }
  } else if (taxonomySlug == Taxonomy.ArticleType) {
    const articleType = articleTypes.find(a => a.slug === pageSlug);
    if (articleType) {
      pageName = articleType.name;
    }
  }
  pageName = pageName || pageSlug?.toString().replace(/-/g, ' ');
  return pageName
    ?.split(' ')
    .map(word => word[0].toUpperCase() + word.substring(1))
    .join(' ');
}

export function GeneralResults({
  taxonomySlug,
  pageSlug,
  ...otherProps
}: Omit<React.ComponentProps<typeof GeneralResultsComp>, 'generalResults' | 'articleType'> & {
  taxonomySlug: string;
  pageSlug: string;
}) {
  const loadingComponent = React.useCallback(() => <GeneralResultsSkeleton />, []);

  // TODO: I am sure those hook calls can be merged into one response somehow
  return (
    <DynamicComponent<WordpressPost[], Article[]>
      height={544}
      fetch={function useFetch() {
        return useGeneralResults({
          taxonomySlug,
          pageSlug,
        });
      }}
      transform={posts => posts.map(WordpressConverter.convertPost).filter(WordpressConverter.filterOutPreviewContent)}
      loadingComponent={loadingComponent}
    >
      {({ transformed: posts }) => (
        <DynamicComponent<WordpressTag[], Category[]>
          height={544}
          fetch={function useFetch() {
            return useContentTags({
              _fields: BlogNav.requiredWPFields.tags,
            });
          }}
          transform={tags => tags.map(WordpressConverter.convertTag)}
          loadingComponent={loadingComponent}
        >
          {({ transformed: tags }) => (
            <DynamicComponent<WordpressSituation[]>
              height={544}
              fetch={function useFetch() {
                return useTaxonomies('situation');
              }}
              loadingComponent={loadingComponent}
            >
              {({ data: situations }) => {
                if (taxonomySlug == Taxonomy.ArticleType) {
                  return (
                    <DynamicComponent<ArticleType>
                      height={544}
                      fetch={function useFetch() {
                        return useArticleType({
                          slug: pageSlug,
                        });
                      }}
                      loadingComponent={loadingComponent}
                    >
                      {({ data: articleType }) => {
                        return withTitle(
                          {
                            title: getGeneralResultsPageName(taxonomySlug, pageSlug, tags, situations),
                          },
                          <GeneralResultsComp
                            generalResults={posts}
                            articleType={{
                              name: getGeneralResultsPageName(taxonomySlug, pageSlug, tags, situations),
                              slug: pageSlug,
                              yoastMeta: articleType.yoastMeta,
                            }}
                            {...otherProps}
                          />
                        );
                      }}
                    </DynamicComponent>
                  );
                } else {
                  return withTitle(
                    {
                      title: getGeneralResultsPageName(taxonomySlug, pageSlug, tags, situations),
                    },
                    <GeneralResultsComp
                      generalResults={posts}
                      articleType={{
                        name: getGeneralResultsPageName(taxonomySlug, pageSlug, tags, situations),
                        slug: pageSlug,
                        yoastMeta: undefined,
                      }}
                      {...otherProps}
                    />
                  );
                }
              }}
            </DynamicComponent>
          )}
        </DynamicComponent>
      )}
    </DynamicComponent>
  );
}
